import Storage from './Storage';
import { apiErrorFromResponse } from './ApiError';
import { BaseAPIConfig, ExtendedRequestInit } from './types';

export default class BaseAPI {
  private cache = new Map();
  private activeRequests: Map<string, { cancel: () => void }> = new Map();
  private config: BaseAPIConfig;

  constructor(config: BaseAPIConfig) {
    this.config = config;
  }

  private getHeaders(headers: RequestInit['headers'] = {}) {
    const baseHeaders: Record<string, string> = {
      ...(headers as Record<string, string>),
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Origin: this.config.origin,
    };

    // Add authorization header if authHeaderName is specified
    if (this.config.authHeaderName) {
      baseHeaders[this.config.authHeaderName] =
        `Bearer ${Storage.get('token')}`;
    }

    // Add any additional headers from config
    if (this.config.getAdditionalHeaders) {
      Object.assign(baseHeaders, this.config.getAdditionalHeaders());
    }

    return baseHeaders;
  }

  // Poll should never cache
  poll<T>(url: string, interval: number, callback: (data: T | null) => void) {
    this.get<T>(url, { noCache: true }).then((data) => {
      callback(data);
    });
    const poller = setInterval(() => {
      this.get<T>(url, { noCache: true }).then((data) => {
        callback(data);
      });
    }, interval);
    return { poller, cancel: this.activeRequests.get(url)?.cancel };
  }

  get<T>(url: string, options: ExtendedRequestInit = {}) {
    if (this.cache.has(url) && !options.noCache) {
      return this.cache.get(url) as Promise<T>;
    }
    if (this.activeRequests.has(url)) {
      this.activeRequests.get(url)?.cancel(); // Cancel the previous request if still active
    }
    const controller = new AbortController();
    const signal = controller.signal;
    const req = fetch(url, {
      method: 'GET',
      ...options,
      signal,
      headers: this.getHeaders(options.headers),
    })
      .then(async (response) => {
        this.cache.delete(url);
        return await this.checkResponse<T>(response);
      })
      .catch((error) => {
        this.cache.delete(url); // Clear cache on error
        this.activeRequests.delete(url); // Remove from active requests on error
        throw error;
      });
    if (!options.noCache) {
      this.cache.set(url, req);
    }
    this.activeRequests.set(url, { cancel: () => controller.abort() });
    return req;
  }

  post<T>(url: string, options: RequestInit = {}) {
    if (this.activeRequests.has(url)) {
      this.activeRequests.get(url)?.cancel(); // Cancel the previous request if still active
    }
    const controller = new AbortController();
    const signal = controller.signal;
    const req = fetch(url, {
      method: 'POST',
      headers: this.getHeaders(options.headers),
      ...options,
      signal,
    })
      .then(async (response) => {
        this.cache.delete(url);
        return await this.checkResponse<T>(response);
      })
      .catch((error) => {
        this.activeRequests.delete(url); // Remove from active requests on error
        throw error;
      });
    this.activeRequests.set(url, { cancel: () => controller.abort() });
    return req;
  }

  put<T>(url: string, options: RequestInit = {}) {
    if (this.activeRequests.has(url)) {
      this.activeRequests.get(url)?.cancel(); // Cancel the previous request if still active
    }
    const controller = new AbortController();
    const signal = controller.signal;
    const req = fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(options.headers),
      ...options,
      signal,
    })
      .then(async (response) => {
        this.cache.delete(url);
        return await this.checkResponse<T>(response);
      })
      .catch((error) => {
        this.activeRequests.delete(url); // Remove from active requests on error
        throw error;
      });
    this.activeRequests.set(url, { cancel: () => controller.abort() });
    return req;
  }

  delete<T>(url: string, options: RequestInit = {}) {
    if (this.activeRequests.has(url)) {
      this.activeRequests.get(url)?.cancel(); // Cancel the previous request if still active
    }
    const controller = new AbortController();
    const signal = controller.signal;
    const req = fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(options.headers),
      ...options,
      signal,
    })
      .then(async (response) => {
        this.cache.delete(url);
        return await this.checkResponse<T>(response);
      })
      .catch((error) => {
        this.activeRequests.delete(url); // Remove from active requests on error
        throw error;
      });
    this.activeRequests.set(url, { cancel: () => controller.abort() });
    return req;
  }

  private async parseResponse<T>(response: Response) {
    // Check if there are any special endpoint handlers configured
    if (this.config.specialEndpointHandlers) {
      for (const [endpoint, handler] of Object.entries(
        this.config.specialEndpointHandlers
      )) {
        if (response.url.indexOf(endpoint) > -1) {
          return (await handler(response)) as T;
        }
      }
    }

    const text = await response.text();
    if (!text) return null as T;
    return JSON.parse(text) as T;
  }

  private async checkResponse<T>(response: Response) {
    // Clear request from active requests
    this.activeRequests.delete(response.url);
    if (response.status === 401 || this.config.isUnauthorized?.(response)) {
      console.error(`❌ ${response.status} Unauthorized from: ${response.url}`);
      if (this.config.onUnauthorized) {
        this.config.onUnauthorized();
      } else {
        Storage.clear();
        window.location.href = '/';
      }
      return null;
    }
    if (response.status >= 200 && response.status < 300) {
      return (await this.parseResponse<T>(response)) as T;
    }
    throw await apiErrorFromResponse(response);
  }
}
